import { useMemo, useState } from "react";
import { ArrowRight, Check, ChevronLeft, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScaledLivePreview } from "@/components/InstagramMockup";
import LandingLivePreview from "@/components/LandingLivePreview";
import { getClubLogoUrl } from "@/lib/bfv";
import type { WizardState } from "./types";

interface Props {
  state: WizardState;
  onNext: (updates: Partial<WizardState>) => void;
  onBack: () => void;
}

type PreviewType = "matchday-post" | "goal" | "endergebnis-post";

const DESIGNS = Array.from({ length: 16 }, (_, index) => String(index + 1));
const PREVIEW_CARDS: { type: PreviewType; label: string; variant: "feed" | "story"; timing: string; benefit: string; caption?: string }[] = [
  { type: "matchday-post", label: "Spielankündigung", variant: "feed", timing: "2 Tage vor dem Spiel", benefit: "Mehr Leute am Platz", caption: "Spieltag! Kommt vorbei 💙" },
  { type: "goal", label: "Tor-Alert", variant: "story", timing: "Sekunden nach dem Tor", benefit: "Die Reaktion kommt, solange das Spiel läuft" },
  { type: "endergebnis-post", label: "Endstand", variant: "feed", timing: "direkt nach Abpfiff", benefit: "Ergebnis draußen, bevor es jemand googelt", caption: "Endstand 3:1 💪" },
];
const PREVIEW_BASE = "https://api.torflash.de/preview";
const LEGACY_DESIGNS: Record<string, string> = {
  klassik: "1",
  klassisch: "1",
  rein: "2",
  modern: "2",
  stadion: "3",
  blitz: "4",
  bold: "4",
};

function normalizeDesign(value: string) {
  const normalized = LEGACY_DESIGNS[value.toLowerCase()] ?? value;
  const number = Number(normalized);
  return Number.isInteger(number) && number >= 1 && number <= 16 ? String(number) : "1";
}

function buildPreviewUrl({
  design,
  type,
  club,
  color,
  secondaryColor,
  crest,
}: {
  design: string;
  type: PreviewType;
  club: string;
  color: string;
  secondaryColor: string;
  crest: string;
}) {
  const params = new URLSearchParams();
  params.set("scale", "0.5");
  params.set("club", club);
  params.set("opp", "Gastverein");
  params.set("pc", color);
  params.set("sc", secondaryColor);
  params.set("crest", crest);
  params.set("crestAway", "");
  params.set("crestVersion", crest || club);
  if (type === "endergebnis-post") {
    params.set("homeScore", "3");
    params.set("awayScore", "1");
  }
  return `${PREVIEW_BASE}/${design}/${type}?${params.toString()}`;
}

export default function StepPreview({ state, onNext, onBack }: Props) {
  const initialDesign = normalizeDesign(state.designId);
  const [design, setDesign] = useState(initialDesign);

  const clubName = state.club?.clubName || "Heimverein";
  const crestUrl = state.branding.logoDataUrl?.startsWith("http")
    ? state.branding.logoDataUrl
    : getClubLogoUrl(state.club) || "";
  const primaryColor = state.branding.primaryColor || "";
  const secondaryColor = state.branding.secondaryColor || "#FFFFFF";

  const thumbnailUrls = useMemo(
    () => Object.fromEntries(DESIGNS.map((id) => [id, buildPreviewUrl({
      design: id,
      type: "matchday-post",
      club: clubName,
      color: primaryColor,
      secondaryColor,
      crest: crestUrl,
    })])),
    [clubName, crestUrl, primaryColor, secondaryColor]
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
          Wähle dein Design
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Wähle aus 16 Designs und sieh dir die Vorschau an.
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0" />
          Es kann einige Sekunden dauern, bis deine Custom-Designs laden.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {DESIGNS.map((id) => {
          const selected = design === id;
          return (
            <Button
              key={id}
              type="button"
              variant="outline"
              onClick={() => setDesign(id)}
              aria-pressed={selected}
              className={`relative h-auto min-w-0 flex-col gap-2 overflow-hidden p-1.5 ${selected ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
            >
              <ScaledLivePreview src={thumbnailUrls[id]} title={`Design ${id}`} height={1350} />
              <span className="pb-1 text-xs font-bold">Design {id}</span>
              {selected ? (
                <span className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                  <Check className="h-4 w-4" />
                </span>
              ) : null}
            </Button>
          );
        })}
      </div>

      <div className="mt-12 border-t border-border pt-12">
        <section className="bg-muted/45 px-4 py-8 md:px-6 md:py-10" aria-labelledby="preview-heading">
          <div className="text-center">
            <h2 id="preview-heading" className="text-xl font-semibold text-foreground">Vorschau: Design {design}</h2>
            <p className="mt-1 text-sm text-muted-foreground">So sehen deine Grafiken auf Instagram aus.</p>
          </div>

          <div className="-mx-4 mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-3 md:gap-5 md:overflow-visible md:px-0">
            {PREVIEW_CARDS.map((card) => {
              const previewUrl = buildPreviewUrl({
                design,
                type: card.type,
                club: clubName,
                color: primaryColor,
                secondaryColor,
                crest: crestUrl,
              });
              return (
                <div
                  key={`${design}-${card.type}`}
                  className="grid h-full min-w-0 w-[92%] shrink-0 snap-center grid-rows-[auto_1fr_auto] rounded-xl border border-border bg-background p-4 shadow-sm sm:w-[62%] md:w-auto"
                >
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-foreground">{card.label}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{card.timing}</p>
                  </div>
                  <div className="h-[650px] min-w-0 w-full">
                    <LandingLivePreview
                    key={`${design}-${card.type}`}
                    src={previewUrl}
                    format={card.variant}
                    clubName={clubName}
                    crestUrl={crestUrl}
                    title={`${card.label} – Design ${design}`}
                    caption={card.caption}
                    showSocialDetails
                    compact
                    />
                  </div>
                  <p className="mt-5 flex items-start gap-2 border-t border-border pt-4 text-sm font-semibold text-foreground">
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{card.benefit}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Beispieldaten. Im Betrieb werden echte Spieldaten eingesetzt.
      </p>

      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" /> Zurück
        </Button>
        <Button variant="hero" className="flex-1" onClick={() => onNext({ designId: design })}>
          Weiter →
        </Button>
      </div>
    </div>
  );
}